import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Stack,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  IconButton,
} from "@chakra-ui/react";

import { Download } from "lucide-react";
import PdfIcon from "assets/svg/pdf.png";
import WordIcon from "assets/svg/word.png";
import ImageIcon from "assets/svg/jpg.png";

import NoDataFound from "components/NoDataGraphics/NoDataFound";
import DocumentViewer from "components/DocumentViewer/DocumentViewer";
import ReusableDrawer from "components/Drawers/ReusableDrawer";
import { formatDate } from "utils/dateFormatter";

const DocumentIcon = ({ type }) => {
  switch (type) {
    case "pdf":
      return <img src={PdfIcon} width={24} />;
    case "docx":
      return <img src={WordIcon} width={24} />;
    case "jpg":
    case "jpeg":
    case "png":
      return <img src={ImageIcon} width={24} />;
    default:
      return null;
  }
};

const ViewResourceDrawer = ({ isOpen, onClose, resource }) => {
  const [isViewerOpen, setViewerOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);

  if (!resource) return null;

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const handleDocumentClick = (doc) => {
    setCurrentDocument({
      url: `${BASE_URL.replace(/\/$/, "")}/${doc.file_path.replace(/^\//, "")}`,
      type: doc.file_extension,
      title: doc.document_type,
      dateUploaded: formatDate(doc.uploaded_at),
    });

    setViewerOpen(true);
  };

  const openDocumentInNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <ReusableDrawer
        isOpen={isOpen}
        onClose={onClose}
        headerContent={<h2>View Resource</h2>}
        bodyContent={
          <Flex direction="column" p={4}>
            {/* Title */}
            <Text fontSize="xl" fontWeight="bold">
              {resource.title}
            </Text>

            <Text fontSize="sm" color="gray.500" mb={4}>
              Created: {formatDate(resource.created_at)}
            </Text>

            {/* Documents */}
            <Box>
              <Text fontWeight="500" mb={3}>
                Files
              </Text>

              {resource.documents?.length > 0 ? (
                <Flex wrap="wrap" gap={3}>
                  {resource.documents.map((doc) => (
                    <Tooltip key={doc.document_id} label="Click to view">
                      <Box
                        p={2}
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        w="calc(90% - 8px)"
                        _hover={{ bg: "gray.100", cursor: "pointer" }}
                        onClick={() => handleDocumentClick(doc)}
                      >
                        <DocumentIcon type={doc.file_extension} />

                        <Box ml={3}>
                          <Text fontWeight="bold">
                            {doc.document_type}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            {formatDate(doc.uploaded_at)}
                          </Text>
                        </Box>
                      </Box>
                    </Tooltip>
                  ))}
                </Flex>
              ) : (
                <NoDataFound message="No files uploaded" />
              )}
            </Box>
          </Flex>
        }
      />

      {/* DOCUMENT VIEWER MODAL */}
      <Modal isOpen={isViewerOpen} onClose={() => setViewerOpen(false)}>
        <ModalOverlay />
        <ModalContent maxW="500px">
          <ModalHeader>Document Viewer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {currentDocument ? (
              <>
                <DocumentViewer documents={[currentDocument]} />

                <IconButton
                  icon={<Download />}
                  aria-label="download"
                  mt={4}
                  onClick={() => openDocumentInNewTab(currentDocument.url)}
                />
              </>
            ) : (
              <NoDataFound message="No document selected" />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ViewResourceDrawer;